import { callPeerNumber, useSoftphone } from "@dialstack/sdk-react/softphone";
import { useRef } from "react";
import { shallowEqual } from "react-redux";

import { useAppSelector } from "app/hooks";

import type { IncomingCallPop } from "../incoming-call-pop.slice";
import { selectFreshPops } from "../incoming-call-pop.slice";
import { phonesMatch } from "../phone-match.util";

type SoftphoneCall = ReturnType<typeof useSoftphone>["calls"][number];

export type InboundCallMatch = {
  call: SoftphoneCall;
  // The SDK / provider call id.
  callId: string;
  // The websocket pop carrying the backend's match, when one was joined.
  pop: IncomingCallPop | null;
  backendCallId: number | null;
};

const sameNumber = (a: string, b: string) =>
  phonesMatch(a, b) || (a.trim() !== "" && a.trim() === b.trim());

const sameCaller = (pop: IncomingCallPop, number: string) =>
  pop.transfer
    ? sameNumber(pop.fromNumber, number)
    : phonesMatch(pop.fromNumber, number);

export const isRinging = (call: SoftphoneCall) =>
  call.state === "ringing" || call.state === "trying";

// Names which call a card belongs to once more than one is live: a second
// call rings in beside the current one, and answering it holds the first.
export function inboundCallLabel(call: SoftphoneCall) {
  if (isRinging(call)) return "Incoming call";
  return call.state === "held" ? "On hold" : "Current call";
}

// Every live inbound SDK call joined to its websocket pop, the ringing ones
// first — they are the ones waiting on a decision. The pop's callId is
// the provider call id (Call.dialstack_call_id), which is also what the SDK
// puts in call.id, so the join is by id first. The last-10-digit phone join
// is kept only as a one-to-one fallback (older pops, or an id mismatch on some
// provider path); when two live calls or two pops share a number the fallback
// declines rather than guessing, and the caller is shown without actions.
// A transferred call comes from a colleague's extension, which the 10-digit
// match can never join, so transfer pops fall back on the exact extension.
//
// A joined pop is pinned by call id so the match survives the answer (the
// call leaves `incomingCalls`) and the pop's TTL on long calls, and drops only
// when the call itself ends. A fresh pop for the same call id still replaces
// the pinned one: a transferred leg's `call.transfer` pop can land after its
// plain `call.incoming` pop, and it is the one that names the patient.
export default function useInboundCallPops(): InboundCallMatch[] {
  // `incomingCalls`/`calls` come from the provider CONTEXT (useSoftphone),
  // never from the SDK's `useCalls(options)` — that one is the low-level hook
  // that CREATES a phone connection and throws without its required options.
  const { incomingCalls, calls } = useSoftphone();
  const now = Date.now();
  // The selector filters into a new array every time; shallowEqual keeps an
  // unrelated store update from re-rendering every consumer.
  const freshPops = useAppSelector(
    (state) => selectFreshPops(state, now),
    shallowEqual
  );

  // Ringing calls live in `incomingCalls`; answered ones move to `calls`.
  // Union by id so a match follows the call across that transition.
  const inboundCalls = [
    ...incomingCalls,
    ...calls.filter(
      (call) =>
        call.direction === "inbound" &&
        !incomingCalls.some((incoming) => incoming.id === call.id)
    )
  ];

  const pinnedPops = useRef(new Map<string, IncomingCallPop>());
  // Pops whose call has ended. A colleague who transfers a patient and then
  // rings again a minute later must not have the fallback hand the second call
  // the first one's patient.
  const spentPopIds = useRef(new Set<string>());

  const matches = inboundCalls.map((call) => {
    let pop =
      freshPops.find((candidate) => candidate.callId === call.id) ??
      pinnedPops.current.get(call.id);
    if (!pop) {
      const number = callPeerNumber(call);
      const popsByNumber = freshPops.filter(
        (candidate) =>
          !spentPopIds.current.has(candidate.callId) &&
          sameCaller(candidate, number)
      );
      const callsByNumber = inboundCalls.filter((candidate) =>
        sameNumber(callPeerNumber(candidate), number)
      );
      if (popsByNumber.length === 1 && callsByNumber.length === 1) {
        pop = popsByNumber[0];
      }
    }
    if (pop) pinnedPops.current.set(call.id, pop);
    return {
      call,
      callId: call.id,
      pop: pop ?? null,
      backendCallId: pop?.backendCallId ?? null
    };
  });

  // Forget matches for calls that have ended so the map can't grow unbounded
  // and a recycled call id can't resurrect a stale match.
  const liveCallIds = new Set(inboundCalls.map((call) => call.id));
  for (const [callId, pop] of [...pinnedPops.current.entries()]) {
    if (liveCallIds.has(callId)) continue;
    pinnedPops.current.delete(callId);
    spentPopIds.current.add(pop.callId);
  }
  const freshPopIds = new Set(freshPops.map((pop) => pop.callId));
  for (const popId of [...spentPopIds.current]) {
    if (!freshPopIds.has(popId)) spentPopIds.current.delete(popId);
  }

  // Array#sort is stable, so calls keep the SDK's order within each group.
  return matches.sort(
    (a, b) => Number(isRinging(b.call)) - Number(isRinging(a.call))
  );
}
